/**
 * 発注書・入荷の明細を並べ替える。
 *
 * 🔑 目的は**仕入先の請求書との突合**＝手元の並びを相手の書類に合わせられること。
 * 発注書（`PurchaseOrderLine`）と入荷（`PlacedOrderItem`）で行の型が違うので、
 * 「SKU・商品名・数量」への射影だけを受け取る形にして両方から使う。
 */

/** 並べ替えに使う3項目だけへ落とした明細。 */
export interface SortableLine {
  sku: string | null;
  title: string | null;
  qty: number;
}

export const LINE_SORT_KEYS = ["default", "sku", "title", "qty"] as const;

export type LineSortKey = (typeof LINE_SORT_KEYS)[number];

/**
 * 文字列を比較する。
 *
 * 🔴 `numeric: true` が要る＝素の比較では **"SKU-10" < "SKU-2"** になり、
 * 桁が繰り上がる SKU 体系の店で並びが崩れて突合の役に立たない。
 * `sensitivity: "base"` で大文字小文字と濁点の揺れを同一視する（HONEY と honey を離さない）。
 */
function compareText(a: string, b: string, locale: string): number {
  return a.localeCompare(b, locale, { numeric: true, sensitivity: "base" });
}

/**
 * 値が無い明細を末尾へ落とすための比較。両方 null なら 0（＝元の順序が残る）。
 * 先頭に空行が並ぶと、いちばん見たい行が押し下げられて突合の邪魔になる。
 */
function compareNullable(
  a: string | null,
  b: string | null,
  locale: string,
): number {
  const left = (a ?? "").trim();
  const right = (b ?? "").trim();
  if (left === "" && right === "") return 0;
  if (left === "") return 1;
  if (right === "") return -1;
  return compareText(left, right, locale);
}

/**
 * `lines` を `key` の順に並べ替えた**新しい配列**を返す（入力は書き換えない）。
 *
 * 同値のときは 0 を返す＝`Array.prototype.sort` が安定なので元の順序が残り、
 * 並べ替えのたびに同点の行が入れ替わることがない。
 */
export function sortLines<T>(
  lines: readonly T[],
  key: LineSortKey,
  project: (line: T) => SortableLine,
  locale = "ja",
): T[] {
  const copy = [...lines];
  if (key === "default") return copy;

  return copy.sort((a, b) => {
    const left = project(a);
    const right = project(b);
    if (key === "sku") return compareNullable(left.sku, right.sku, locale);
    if (key === "title") return compareNullable(left.title, right.title, locale);
    // 数量は多い順＝発注の重い行から確認できる。
    return right.qty - left.qty;
  });
}
